/**
 * Utility functions for detecting external links and determining when to show warnings
 */
import DevsiteEnv from '../DevsiteEnv';

/**
 * Controlled by `EXTERNAL_LINK_BLOCKING_ENABLED` in the env file. Reads `DevsiteEnv` on each call
 * so tests can mutate env without stale module-level toggles.
 */
export function isExternalLinkWarningEnvironment(): boolean {
  return DevsiteEnv.EXTERNAL_LINK_BLOCKING_ENABLED;
}

/**
 * Determines if a URL is external to the devsite domain
 * @param url - The URL to check
 * @returns true if the URL is external to devsite domain, false if internal or invalid
 */
export function isExternalUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Handle non-HTTP protocols (mailto, tel, javascript, etc.)
  if (url.includes(':') && !url.startsWith('http') && !url.startsWith('//')) {
    return false;
  }

  // Handle relative URLs and hash URLs (but not protocol-relative URLs)
  if ((url.startsWith('/') && !url.startsWith('//')) || url.startsWith('#') || url.startsWith('?')) {
    return false;
  }

  try {
    // Handle protocol-relative URLs by adding a protocol
    let normalizedUrl = url;
    if (url.startsWith('//')) {
      normalizedUrl = `https:${url}`;
    }

    // For relative URLs without protocol, treat as internal
    if (!normalizedUrl.includes('://')) {
      return false;
    }

    const urlObj = new URL(normalizedUrl);
    const hostname = urlObj.hostname.toLowerCase();

    // Get the devsite domain
    const devsiteDomain = getDevsiteDomain();

    if (!devsiteDomain) {
      // If no devsite domain is configured, treat all URLs as internal for safety
      return false;
    }

    // Check if the hostname matches the exact devsite domain (no subdomains allowed)
    const lowerDevsiteDomain = devsiteDomain.toLowerCase();
    return hostname !== lowerDevsiteDomain;
  } catch {
    // If URL parsing fails, treat as internal for safety
    return false;
  }
}

/**
 * Determines if we should show an external link warning based on URL and environment
 * @param url - The URL being navigated to
 * @returns true if warning should be shown, false otherwise
 */
export function shouldShowExternalLinkWarning(url: string): boolean {
  if (!isExternalLinkWarningEnvironment()) {
    return false;
  }

  return isExternalUrl(url);
}

/**
 * Gets the devsite domain for comparison
 * @returns The devsite domain or null if not configured
 */
export function getDevsiteDomain(): string | null {
  return DevsiteEnv.DEVSITE_DOMAIN || null;
}
